import api from './api.js';

const NotificationService = {
  /**
   * Получение уведомлений пользователя
   * @param {Object} options - Параметры запроса
   * @param {number} options.limit - Количество уведомлений на странице
   * @param {number} options.offset - Смещение для пагинации
   * @param {boolean} options.read - Фильтр по статусу прочтения
   * @param {string} options.type - Фильтр по типу уведомления
   * @param {string} options.leagueSlug - Фильтр по лиге
   * @returns {Promise<Object>} - Объект с уведомлениями и метаданными
   */
  async getNotifications(options = {}) {
    const params = new URLSearchParams();

    if (options.limit) params.append('limit', options.limit);
    if (options.offset) params.append('offset', options.offset);
    if (options.read !== undefined) params.append('read', options.read);
    if (options.type) params.append('type', options.type);
    if (options.leagueSlug) params.append('leagueSlug', options.leagueSlug);

    const queryString = params.toString();
    const url = queryString ? `/notifications?${queryString}` : '/notifications';

    return api.get(url);
  },
  
  /**
   * Получение количества непрочитанных уведомлений
   * @param {string} leagueSlug - Фильтр по лиге (опционально)
   * @returns {Promise<Object>} - Объект с количеством непрочитанных уведомлений
   */
  async getUnreadCount(leagueSlug = null) {
    const params = new URLSearchParams();
    if (leagueSlug) params.append('leagueSlug', leagueSlug);
    const queryString = params.toString();
    const url = queryString ? `/notifications/unread-count?${queryString}` : '/notifications/unread-count';
    return api.get(url);
  },

  /**
   * Получение общего количества уведомлений
   * @param {string} leagueSlug - Фильтр по лиге (опционально)
   * @returns {Promise<Object>} - Объект с общим количеством уведомлений
   */
  async getTotalCount(leagueSlug = null) {
    const params = new URLSearchParams();
    if (leagueSlug) params.append('leagueSlug', leagueSlug);
    const queryString = params.toString();
    const url = queryString ? `/notifications/count?${queryString}` : '/notifications/count';
    return api.get(url);
  },
  
  /**
   * Отметка уведомления как прочитанного
   * @param {string} id - ID уведомления
   * @returns {Promise<Object>} - Обновленное уведомление
   */
  async markAsRead(id) {
    return api.put(`/notifications/${id}/read`);
  },
  
  /**
   * Отметка всех уведомлений как прочитанных
   * @param {string} leagueSlug - Фильтр по лиге (опционально)
   * @returns {Promise<Object>} - Результат операции
   */
  async markAllAsRead(leagueSlug = null) {
    const params = new URLSearchParams();
    if (leagueSlug) params.append('leagueSlug', leagueSlug);
    const queryString = params.toString();
    const url = queryString ? `/notifications/read-all?${queryString}` : '/notifications/read-all';
    return api.post(url);
  },
  
  /** Delete all notifications */
  async clearAll(leagueSlug = null) {
    const params = new URLSearchParams();
    if (leagueSlug) params.append('leagueSlug', leagueSlug);
    const queryString = params.toString();
    const url = queryString ? `/notifications/clear-all?${queryString}` : '/notifications/clear-all';
    return api.delete(url);
  },
  
  /**
   * Удаление уведомления
   * @param {string} id - ID уведомления
   * @returns {Promise<Object>} - Результат операции
   */
  async deleteNotification(id) {
    return api.delete(`/notifications/${id}`);
  },

  /**
   * Respond to a bid
   * @param {string} bidId - Bid ID
   * @param {string} action - Action to take (accept, reject, counter)
   * @param {number} counterOffer - Counter offer amount (only for counter action)
   * @param {string} leagueId - League ID
   * @returns {Promise<Object>} - Response from the API
   */
  async respondToBid(bidId, action, counterOffer = null, leagueId = null) {
    const payload = { action };
    if (action === 'counter' && counterOffer !== null) {
      // Backend expects an object containing amount (and optionally contractOffer)
      payload.counterOffer = typeof counterOffer === 'number' ? { amount: counterOffer } : counterOffer;
    }
    if (leagueId) {
      payload.leagueId = leagueId;
    }
    return api.post(`/transfers/bids/${bidId}/respond`, payload);
  }
};

export default NotificationService; 
